import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes } from 'react';

import { ptBR } from '@/i18n/pt-BR';
import { cn } from '@/lib/utils';

import { Spinner } from './Spinner';

/**
 * Botão do Design System (ADR-0065). O primário é o **único objeto sólido** da
 * interface — todo o resto é desenho de linha —, por isso nenhuma variante leva
 * sombra: elevação decorativa foi o que a estética anterior usava para separar
 * planos, e aqui quem separa é o hairline.
 *
 * Movimento do pacote: 120ms (`duration-fast`) com o easing do sistema, e press
 * = `translateY(1px)`. Foco de teclado sempre no outline de 2px em aço com
 * offset 2px — nunca o ring default.
 *
 * Rampa do preenchimento sólido deslocada um degrau (ADR-0065 §9): `accent-600`
 * dá 5,8:1 com texto claro; o aço base dá 3,7:1 e reprova em AA.
 *
 * `danger` é desenho de linha, não bloco vermelho (E54.F2.C01): a cor de sinal
 * do pacote fica reservada a resultado e direção, e um segundo preenchimento
 * sólido faria o primário deixar de ser o único objeto sólido da tela.
 *
 * E200.F3 (GUS-220, ADR-0143 §4): `loading` é o pedido em curso — o botão desabilita (o segundo
 * clique não repete o pedido), anuncia `aria-busy` e leva o círculo girando com "Aguarde…" para o
 * leitor de tela; o rótulo visível fica por conta de quem chama ("Salvando…", "Baixando…").
 */
const buttonVariants = cva(
  'relative inline-flex select-none items-center justify-center gap-sc-2 whitespace-nowrap font-sans font-semibold transition-colors duration-fast ease-sc focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:cursor-not-allowed disabled:opacity-45',
  {
    variants: {
      variant: {
        primary:
          'border border-accent-600 bg-accent-600 text-fg-onInverse hover:bg-accent-700 active:translate-y-px active:bg-accent-800',
        secondary:
          'border border-border bg-transparent text-fg hover:bg-fg/[0.07] active:translate-y-px active:bg-fg/[0.14]',
        ghost: 'border border-transparent text-fg hover:bg-fg/[0.07] active:bg-fg/[0.14]',
        danger:
          'border border-danger bg-transparent text-danger-700 hover:bg-danger-50 active:translate-y-px active:bg-danger-50',
        link: 'h-auto border-0 p-0 text-accent-700 underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-8 px-2.5 text-[13px]',
        default: 'h-9 px-3.5 text-body-sm',
        lg: 'h-10 px-5 text-body-md',
        icon: 'h-9 w-9 p-0',
        /** Botão de bloco das pranchas (entrar, ação final de formulário). */
        block: 'h-[42px] w-full px-4 text-body-md',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** E200.F3: o pedido deste botão está em curso; desabilita e anuncia a espera. */
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant,
    size,
    asChild = false,
    loading = false,
    type,
    disabled,
    children,
    ...props
  },
  ref,
) {
  const Comp = asChild ? Slot : 'button';
  const ocupado = loading && !asChild;
  return (
    <Comp
      ref={ref}
      className={cn(buttonVariants({ variant, size, className }))}
      {...(asChild ? {} : { type: type ?? 'button' })}
      disabled={disabled === true || ocupado}
      {...(ocupado ? { 'aria-busy': true, 'data-loading': 'true' } : {})}
      {...props}
    >
      {ocupado ? (
        <>
          <Spinner size="sm" className="border-current" />
          <span className="sr-only">{ptBR.common.aguarde}</span>
          {children}
        </>
      ) : (
        children
      )}
    </Comp>
  );
});

export { buttonVariants };
